import * as base64js from 'base64-js'
import { defineModule, localActionContext, localGetterContext } from 'direct-vuex'
import { Query, SentenceRecordingType, SentenceType, TagType, WordRecordingType } from 'types/types'
import { VueLogger } from 'vue-logger-plugin'
import { ActionContext } from 'vuex'

import { runStoreMutation, showStoreError } from '@/store/mutationHelpers'
import { useApollo } from '@/util/apolloClient'
const { apolloClient } = useApollo()

import {
  createBlobUrlFromBytes,
  DEFAULT_PITCH_SHIFT,
  HTMLAudioWrapper,
  PitchedSentenceOrSegmentAudio,
  PitchShiftType,
  SentenceRecordingMetadataWithPitchedAudios,
} from '@/util/AudioUtil'
import { cloneRaw } from '@/util/util'

import DeleteSentenceRecording from '../mutations/DeleteSentenceRecording.graphql'
import ModifySentenceRecording from '../mutations/ModifySentenceRecording.graphql'
import MySentenceRecordingsQuery from '../queries/MySentenceRecordingsQuery.graphql'
import SegmentRecordingContents from '../queries/SegmentRecordingContents.graphql'
import SentenceRecordingContents from '../queries/SentenceRecordingContents.graphql'
import SentenceRecordingQuery from '../queries/SentenceRecordingQuery.graphql'

// [sentenceId]: {
//   [sentenceRecordingId]: {
//     sentenceRecording: SentenceRecordingType;
//     updating: boolean;
//     audio: {
//       [pitchShift]: PitchedSentenceOrSegmentAudio
//     }
//   } as SentenceRecordingMetadataWithPitchedAudios
// }

type SentenceRecordingsMap = {
  [sentenceId: string]: {
    [sentenceRecordingId: string]: SentenceRecordingMetadataWithPitchedAudios
  }
}

/** Shown when a recording cannot be saved because it has no pronunciation. */
export const PRONUNCIATION_REQUIRED_MESSAGE = 'Please choose a pronunciation for this recording before changing it.'

interface RootState {}

interface RecordingsState {
  updating: boolean
  loading: boolean
  logger: VueLogger | null
  recordings: SentenceRecordingsMap
}

const nullPitchedSentenceOrSegmentAudio: PitchedSentenceOrSegmentAudio = {
  downloading: false,
  pitchShift: DEFAULT_PITCH_SHIFT,
  segmentHTMLAudioWrappers: new Array<HTMLAudioWrapper>(),
  sentenceHTMLAudioWrapper: {
    audio: null,
    blobUrl: null,
    playStatus: null,
  } as HTMLAudioWrapper,
} as PitchedSentenceOrSegmentAudio

const resetState = (): RecordingsState => {
  return {
    loading: false,
    logger: null,
    recordings: {} as SentenceRecordingsMap,
    updating: false,
  }
}

function releaseMemory(audio: HTMLAudioWrapper) {
  if (audio?.audio) {
    window.URL.revokeObjectURL(audio.audio.src)
  }
}

function releaseRecordingAudio(recording: SentenceRecordingMetadataWithPitchedAudios) {
  for (const pitchedAudio of Object.values(recording.audio)) {
    releaseMemory(pitchedAudio.sentenceHTMLAudioWrapper)
    for (const wordIndex of Object.keys(pitchedAudio.segmentHTMLAudioWrappers).map(Number)) {
      releaseMemory(pitchedAudio.segmentHTMLAudioWrappers[wordIndex])
    }
  }
}

const audio = defineModule({
  // [sentenceId]: {
  //   [sentenceRecordingId]: {
  //     sentenceRecording: SentenceRecordingType;
  //     updating: boolean;
  //     audio: {
  //       [pitchShift]: PitchedSentenceOrSegmentAudio
  //     }
  //   } as SentenceRecordingMetadataWithPitchedAudios
  // }
  actions: {
    /** Delete a recording on the server, then from the store. Resolves false (and shows why) if it could not be deleted. */
    async deleteRecording(context, payload: { sentenceRecording: SentenceRecordingType }): Promise<boolean> {
      const { commit, state } = audioActionContext(context)

      commit.setUpdating(true)
      try {
        const sentenceId = payload.sentenceRecording.sentence.id
        if (!(sentenceId in state.recordings)) {
          console.error('WARNING: store.audio, deleteRecording with unknown sentenceId')
          return false
        }

        if (!(payload.sentenceRecording.id in state.recordings[sentenceId])) {
          console.error('WARNING: store.audio, deleteRecording with unknown sentenceRecordingId')
          return false
        }

        const sentenceRecordingMetadataWithPitchedAudios = state.recordings[sentenceId][payload.sentenceRecording.id]
        if (sentenceRecordingMetadataWithPitchedAudios.sentenceRecording?.id !== payload.sentenceRecording.id) {
          console.error('WARNING: store.audio, deleteRecording weird inconsistent state')
          return false
        }

        const result = await runStoreMutation(
          context,
          DeleteSentenceRecording,
          { sentenceRecording: payload.sentenceRecording.id },
          'deleteSentenceRecording',
        )
        if (!result) {
          return false
        }
        commit.deleteRecording({ sentenceRecording: payload.sentenceRecording })
        return true
      } finally {
        commit.setUpdating(false)
      }
    },

    fetchAudio(
      context,
      payload: {
        sentenceRecording: SentenceRecordingType | null
        pitchShift: PitchShiftType | null
        cache: boolean
      },
    ): Promise<void> {
      const { commit, state } = audioActionContext(context)

      const requestedRecording = payload.sentenceRecording
      if (!requestedRecording) {
        console.error('WARNING: fetchAudio, no payload.sentenceRecording')
        return Promise.reject()
      }

      commit.setFetchingAudio({
        b: true,
        sentenceRecording: requestedRecording,
      })
      const pitchShift = payload.pitchShift ?? DEFAULT_PITCH_SHIFT

      return new Promise<void>((resolve, reject) => {
        // there is a recording for this sentence; fetch its contents
        const sentenceId = requestedRecording.sentence.id
        if (!(sentenceId in state.recordings)) {
          console.error('WARNING: store.audio, fetchAudio with unknown sentenceId')
          commit.setFetchingAudio({
            b: false,
            sentenceRecording: requestedRecording,
          })
          resolve()
          return
        }

        const sentenceRecordingId = requestedRecording.id
        if (!(sentenceRecordingId in state.recordings[sentenceId])) {
          console.error('WARNING: store.audio, fetchAudio with unknown sentenceRecordingId')
          commit.setFetchingAudio({
            b: false,
            sentenceRecording: requestedRecording,
          })
          resolve()
          return
        }

        const sentenceRecordingMetadataWithPitchedAudios = state.recordings[sentenceId][sentenceRecordingId]
        const { sentenceRecording } = sentenceRecordingMetadataWithPitchedAudios

        if (!sentenceRecording) {
          state.logger?.error && state.logger.error('unexpected !sentenceRecording in fetchAudio')
          commit.setLoading(false)
          reject()
          return
        }

        if (!payload.cache || !(pitchShift in state.recordings[sentenceId][sentenceRecordingId].audio)) {
          state.logger?.debug &&
            state.logger.debug(`pitchShift ${pitchShift} not in state.recordings[sentenceId][sentenceRecordingId].audio`)
          commit.initPitchedAudio({ pitchShift, sentenceId, sentenceRecordingId })
        } else {
          state.logger?.debug && state.logger.debug(`!payload.cache or pitchShifted already in state.recordings`)
        }

        const pitchedSentenceOrSegmentAudio: PitchedSentenceOrSegmentAudio =
          state.recordings[sentenceId][sentenceRecordingId].audio[pitchShift]

        const promises: Array<Promise<unknown>> = []
        if (
          !payload.cache ||
          !pitchedSentenceOrSegmentAudio.sentenceHTMLAudioWrapper.audio ||
          !pitchedSentenceOrSegmentAudio.sentenceHTMLAudioWrapper.audio.src
        ) {
          state.logger?.debug && state.logger.debug(`!audio or !blobUrl for ${sentenceRecordingId}, pitchShift ${pitchShift}`)
          const p = apolloClient.query<Pick<Query, 'sentenceRecordingContents'>>({
            fetchPolicy: 'network-only',
            query: SentenceRecordingContents,
            variables: {
              id: sentenceRecordingId,
              pitchShift: parseFloat(String(pitchShift)),
            },
          })
          promises.push(p)

          p.then((sentenceResponse) => {
            state.logger?.debug && state.logger.debug('promise sentenceResponse =')
            state.logger?.debug && state.logger.debug(sentenceResponse)
            const contents = sentenceResponse.data?.sentenceRecordingContents
            if (!contents?.ok || !contents.blob) {
              console.error('fetchAudio: sentenceRecordingContents failed:', contents?.errors ?? 'no response')
              return
            }
            const compressedWavFile = base64js.toByteArray(contents.blob)
            state.logger?.debug && state.logger.debug('promise compressedWavFile =')
            state.logger?.debug && state.logger.debug(compressedWavFile)
            const blobUrl = createBlobUrlFromBytes(compressedWavFile) // can't use decomposition; will conflict with name 'audio'
            state.logger?.debug && state.logger.debug('blobUrl =')
            state.logger?.debug && state.logger.debug(blobUrl)
            commit.addAudio({
              blobUrl,
              pitchShift: pitchShift.toString(),
              sentenceRecordingMetadataWithPitchedAudios,
              wordIndex: -1,
            })

            state.logger?.debug && state.logger.debug('sanity check post-addAudio()')
            if (!payload.cache || !(pitchShift in state.recordings[sentenceId][sentenceRecordingId].audio)) {
              state.logger?.debug &&
                state.logger.debug(`pitchShift ${pitchShift} STILL not in state.recordings[sentenceId][sentenceRecordingId].audio`)
            }
          })
        } else {
          state.logger?.debug && state.logger.debug(`!payload.cache or .audio or .audio.src already exists`)
          state.logger?.debug && state.logger.debug('payload.cache =')
          state.logger?.debug && state.logger.debug(payload.cache)
        }

        // sets this.segmentHTMLAudioWrappers for all the segments in this.sentence.id
        if (!payload.cache || !pitchedSentenceOrSegmentAudio.segmentHTMLAudioWrappers.length) {
          commit.initSegmentSlots({ pitchShift, segmentCount: sentenceRecording.segments.length, sentenceId, sentenceRecordingId })
        }

        sentenceRecording.segments.forEach((segmentRecording: WordRecordingType, wordIndex: number) => {
          if (!payload.cache || !pitchedSentenceOrSegmentAudio.segmentHTMLAudioWrappers[wordIndex].audio?.src) {
            // state.logger?.debug && state.logger.debug(`fetching segment recording ${wordIndex} for sentencRecordingId ${sentenceRecordingId}`);
            // state.logger?.debug && state.logger.debug(`going to fetch audio for wordIndex ${wordIndex}`);
            const p = apolloClient.query<Pick<Query, 'segmentRecordingContents'>>({
              fetchPolicy: 'network-only',
              query: SegmentRecordingContents,
              variables: {
                id: segmentRecording.id,
                pitchShift: parseFloat(String(pitchShift)),
              },
            })
            promises.push(p)
            p.then((response) => {
              const contents = response.data?.segmentRecordingContents
              if (!contents?.ok || !contents.blob) {
                console.error(`fetchAudio: segmentRecordingContents failed for word ${wordIndex}:`, contents?.errors ?? 'no response')
                return
              }
              const compressedWavFile = base64js.toByteArray(contents.blob)
              const blobUrl = createBlobUrlFromBytes(compressedWavFile)
              commit.addAudio({
                blobUrl,
                pitchShift: pitchShift.toString(),
                sentenceRecordingMetadataWithPitchedAudios,
                wordIndex,
              })
            })
          }
        })

        Promise.allSettled(promises).then(() => {
          commit.setFetchingAudio({ b: false, sentenceRecording })
          resolve()
        })
      })
    },

    async fetchRecording(context: ActionContext<RecordingsState, RootState>, payload: { id: number }): Promise<void> {
      const { commit, dispatch } = audioActionContext(context)

      console.log(`[fetchRecording] querying id=${payload.id}`)
      const response = await apolloClient.query<Pick<Query, 'sentenceRecording'>>({
        fetchPolicy: 'network-only',
        query: SentenceRecordingQuery,
        variables: {
          id: payload.id,
        },
      })
      const sr = response.data?.sentenceRecording
      console.log(
        `[fetchRecording] id=${payload.id} response:`,
        sr ? `uploadedAt=${sr.uploadedAt}, processingEndedAt=${sr.processingEndedAt}, processingSuccess=${sr.processingSuccess}` : 'null',
      )
      if (sr) {
        await dispatch.storeSentenceRecording({ sentenceRecording: sr })
      } else {
        console.warn(`[fetchRecording] id=${payload.id} returned null — removing stale entry from store`)
        commit.deleteRecordingById(String(payload.id))
      }
    },

    async fetchRecordings(context, payload: { sentences: Array<SentenceType> }) {
      const { commit, dispatch, getters, state } = audioActionContext(context)

      const sentenceIds = payload.sentences.map((sentence: SentenceType) => sentence.id)
      console.log('audio.fetchRecordings called, sentenceIds =', sentenceIds)
      state.logger?.debug && state.logger.debug('fetchRecordings, payload.sentences =')
      state.logger?.debug && state.logger.debug(payload.sentences)
      commit.setLoading(true)
      try {
        const response = await apolloClient.query<Pick<Query, 'mySentenceRecordings'>>({
          fetchPolicy: 'network-only',
          query: MySentenceRecordingsQuery,
          variables: {
            sentences: sentenceIds,
          },
        })

        console.log('audio.fetchRecordings response, mySentenceRecordings count =', response.data?.mySentenceRecordings?.length ?? 0)
        console.log('audio.fetchRecordings response.data =', response.data)

        response.data?.mySentenceRecordings?.forEach((sentenceRecording) => {
          if (!sentenceRecording) return
          state.logger?.debug && state.logger.debug('iterating over mySentenceRecordings, sentenceRecording =')
          state.logger?.debug && state.logger.debug(sentenceRecording)
          const sentenceId = sentenceRecording.sentence.id
          const sentenceRecordingId = sentenceRecording.id
          if (sentenceId in getters.recordings && sentenceRecordingId in getters.recordings[sentenceId]) {
            commit.deleteRecording({ sentenceRecording })
          }
          dispatch.storeSentenceRecording({ sentenceRecording })
        })
      } catch (error) {
        console.error('audio.fetchRecordings ERROR:', error)
      }
      commit.setLoading(false)
    },

    /** Save a recording's metadata on the server, then in the store. Resolves false (and shows why) if it could not be saved. */
    async modifySentenceRecording(
      context,
      payload: {
        sentenceRecordingMetadataWithPitchedAudios: SentenceRecordingMetadataWithPitchedAudios
      },
    ): Promise<boolean> {
      const { commit } = audioActionContext(context)
      if (!payload.sentenceRecordingMetadataWithPitchedAudios.sentenceRecording) {
        return false
      }

      const { haftarahMelody, pronunciation, sentenceGroupMelody, torahMelody } =
        payload.sentenceRecordingMetadataWithPitchedAudios.sentenceRecording
      if (!pronunciation) {
        console.error('modifySentenceRecording: recording has no pronunciation set — cannot modify')
        showStoreError(context, PRONUNCIATION_REQUIRED_MESSAGE)
        return false
      }
      commit.setUpdatingState({
        b: true,
        sentenceRecordingMetadataWithPitchedAudios: payload.sentenceRecordingMetadataWithPitchedAudios,
      })
      try {
        const result = await runStoreMutation(
          context,
          ModifySentenceRecording,
          {
            haftarahMelodyStyle: haftarahMelody ? haftarahMelody.id : null,
            pronunciation: pronunciation.id,
            sentenceGroupMelodyStyle: sentenceGroupMelody ? sentenceGroupMelody.id : null,
            sentenceRecording: payload.sentenceRecordingMetadataWithPitchedAudios.sentenceRecording.id,
            tags: payload.sentenceRecordingMetadataWithPitchedAudios.sentenceRecording.tags.map((tag: TagType) => tag.id),
            torahMelodyStyle: torahMelody ? torahMelody.id : null,
          },
          'modifySentenceRecording',
        )
        if (!result) {
          return false
        }
        commit.modifySentenceRecording(payload.sentenceRecordingMetadataWithPitchedAudios)
        return true
      } finally {
        commit.setUpdatingState({
          b: false,
          sentenceRecordingMetadataWithPitchedAudios: payload.sentenceRecordingMetadataWithPitchedAudios,
        })
      }
    },

    releaseRecording(context, payload: { sentenceRecording: SentenceRecordingType | null }) {
      const { commit, state } = audioActionContext(context)

      if (!payload.sentenceRecording) {
        console.error('WARNING: sentenceRecording is empty')
        return
      }

      const sentenceId = payload.sentenceRecording.sentence.id
      if (!(sentenceId in state.recordings)) {
        console.error('WARNING: store.audio, releaseRecording with unknown sentenceId')
        return
      }

      const sentenceRecordingId = payload.sentenceRecording.id
      if (!(sentenceRecordingId in state.recordings[sentenceId])) {
        console.error('WARNING: store.audio, releaseRecording with unknown sentenceRecordingId')
        return
      }

      const sentenceRecordingMetadataWithPitchedAudios = state.recordings[sentenceId][sentenceRecordingId]
      if (sentenceRecordingMetadataWithPitchedAudios.sentenceRecording?.id !== sentenceRecordingId) {
        console.error('WARNING: store.audio, releaseRecording weird inconsistent state')
        return
      }

      commit.deleteRecording({ sentenceRecording: payload.sentenceRecording })
    },

    releaseSentence(context, payload: { sentence: SentenceType }) {
      const { commit, dispatch, state } = audioActionContext(context)

      const sentenceId = payload.sentence.id
      if (!(sentenceId in state.recordings)) {
        // Expected when sentences have no recordings (never fetched into the store)
        return
      }

      // release all recordings for this sentence
      for (const sentenceRecordingId in state.recordings[sentenceId]) {
        const sentenceRecordingMetadataWithPitchedAudios = state.recordings[sentenceId][sentenceRecordingId]
        if (sentenceRecordingMetadataWithPitchedAudios.sentenceRecording?.id !== sentenceRecordingId) {
          continue
        }
        dispatch.releaseRecording({
          sentenceRecording: sentenceRecordingMetadataWithPitchedAudios.sentenceRecording,
        })
      }

      // release the sentence
      commit.deleteSentence({ sentence: payload.sentence })
    },

    reset(context) {
      const { commit, getters } = audioActionContext(context)

      Object.keys(getters.recordings).forEach((sentenceId: string) => {
        Object.keys(getters.recordings[sentenceId]).forEach((sentenceRecordingId: string) => {
          const { sentenceRecording } = getters.recordings[sentenceId][sentenceRecordingId]
          if (sentenceRecording) {
            commit.deleteRecording({ sentenceRecording })
          }
        })
      })

      commit.reset()
    },

    setLogger(context, logger: VueLogger) {
      const { commit } = audioActionContext(context)
      commit.setLogger(logger)
    },

    // utility function for fetchRecording and fetchRecordings
    storeSentenceRecording(context, payload: { sentenceRecording: SentenceRecordingType }) {
      const { commit, state } = audioActionContext(context)

      state.logger?.debug && state.logger.debug(`storeSentenceRecording, sentenceRecording (${payload.sentenceRecording.id}) =`)
      state.logger?.debug && state.logger.debug(payload.sentenceRecording)

      commit.addRecording(payload.sentenceRecording)
    },
  },

  getters: {
    /** Returns the recordings for a given sentence ID as a flat array. */
    getRecordingListForSentence(...args): (sentenceId: string) => SentenceRecordingMetadataWithPitchedAudios[] {
      const { state } = audioGetterContext(args)
      return (sentenceId: string) => Object.values(state.recordings[sentenceId] ?? {})
    },

    /** Returns the recordings sub-map for a given sentence ID, or an empty object if none exist. */
    getRecordingsForSentence(...args): (sentenceId: string) => Record<string, SentenceRecordingMetadataWithPitchedAudios> {
      const { state } = audioGetterContext(args)
      return (sentenceId: string) =>
        (state.recordings[sentenceId] as Record<string, SentenceRecordingMetadataWithPitchedAudios> | undefined) ?? {}
    },

    hasRecordings(...args): boolean {
      const { state } = audioGetterContext(args)
      return Object.keys(state.recordings).length > 0
    },

    /** Checks whether any recordings exist for a given sentence ID. */
    hasRecordingsForSentence(...args): (sentenceId: string) => boolean {
      const { state } = audioGetterContext(args)
      return (sentenceId: string) => sentenceId in state.recordings
    },

    loading(...args): boolean {
      const { state } = audioGetterContext(args)
      return state.loading
    },

    /** Returns the sentence IDs that have at least one recording in the store. */
    recordedSentenceIds(...args): string[] {
      const { state } = audioGetterContext(args)
      return Object.keys(state.recordings)
    },

    /** Whether a recording with the given id is still in the store (across all sentences). */
    recordingExists(...args): (sentenceRecordingId: string) => boolean {
      const { state } = audioGetterContext(args)
      return (sentenceRecordingId: string) => Object.values(state.recordings).some((byRecording) => sentenceRecordingId in byRecording)
    },

    recordings(...args): SentenceRecordingsMap {
      const { state } = audioGetterContext(args)
      return state.recordings
    },

    updating(...args): boolean {
      const { state } = audioGetterContext(args)
      return state.updating
    },
  },

  mutations: {
    addAudio(
      state,
      payload: {
        sentenceRecordingMetadataWithPitchedAudios: SentenceRecordingMetadataWithPitchedAudios
        wordIndex: number // -1 means sentenceHTMLAudioWrapper; otherwise segmentHTMLAudioWrappers
        blobUrl: string
        pitchShift: PitchShiftType
      },
    ) {
      const sentenceId = payload.sentenceRecordingMetadataWithPitchedAudios.sentenceRecording?.sentence.id
      if (!sentenceId) {
        return
      }

      state.logger?.debug && state.logger.debug(`addAudio 1 for sentenceId = ${sentenceId}`)
      if (!(sentenceId in state.recordings)) {
        console.error('WARNING: store.audio, addAudio with unknown sentenceId')
        return
      }

      const sentenceRecordingId = payload.sentenceRecordingMetadataWithPitchedAudios.sentenceRecording?.id

      if (!sentenceRecordingId) {
        return
      }

      if (!(sentenceRecordingId in state.recordings[sentenceId])) {
        console.error('WARNING: store.audio, addAudio with unknown sentenceRecordingId')
        return
      }
      state.logger?.debug && state.logger.debug(`addAudio 2 for sentenceRecordingId = ${sentenceRecordingId}`)

      const { pitchShift } = payload
      if (!(pitchShift in state.recordings[sentenceId][sentenceRecordingId].audio)) {
        console.error('WARNING: store.audio, addAudio with unknown pitchShift')
        return
      }

      const pitchedSentenceOrSegmentAudio: PitchedSentenceOrSegmentAudio =
        state.recordings[sentenceId][sentenceRecordingId].audio[pitchShift]
      state.logger?.debug && state.logger.debug(`addAudio 3 for pitchedSentenceOrSegmentAudio = `)
      state.logger?.debug && state.logger.debug(pitchedSentenceOrSegmentAudio)

      state.logger?.debug && state.logger.debug(`addAudio 4 payload = `)
      state.logger?.debug && state.logger.debug(payload)

      if (payload.wordIndex < 0) {
        //
        // sentence
        //
        if (pitchedSentenceOrSegmentAudio.sentenceHTMLAudioWrapper.audio) {
          state.logger?.debug && state.logger.debug('revoking old sentence objectUrl')
          window.URL.revokeObjectURL(pitchedSentenceOrSegmentAudio.sentenceHTMLAudioWrapper.audio.src)
        }
        state.logger?.debug && state.logger.debug('new Audio for sentence')
        pitchedSentenceOrSegmentAudio.sentenceHTMLAudioWrapper.audio = new Audio(payload.blobUrl)
        state.logger?.debug && state.logger.debug('pitchedSentenceOrSegmentAudio.sentenceHTMLAudioWrapper.audio =')
        state.logger?.debug && state.logger.debug(pitchedSentenceOrSegmentAudio.sentenceHTMLAudioWrapper.audio)

        //
        // segment
        //
      } else {
        if (!pitchedSentenceOrSegmentAudio.segmentHTMLAudioWrappers.length) {
          console.error('WARNING: store.audio, addAudio with empty segmentHTMLAudioWrappers')
          return
        }
        if (pitchedSentenceOrSegmentAudio.segmentHTMLAudioWrappers[payload.wordIndex].audio) {
          state.logger?.debug && state.logger.debug('revoking old segment objectUrl')
          window.URL.revokeObjectURL(pitchedSentenceOrSegmentAudio.segmentHTMLAudioWrappers[payload.wordIndex].audio!.src)
        }
        const audio = new Audio(payload.blobUrl)
        pitchedSentenceOrSegmentAudio.segmentHTMLAudioWrappers[payload.wordIndex].audio = audio
        state.logger?.debug && state.logger.debug('addAudio 4, segment audio=')
        state.logger?.debug && state.logger.debug(audio)
      }
    },

    addRecording(state: RecordingsState, sentenceRecording: SentenceRecordingType) {
      state.logger?.debug && state.logger.debug('addRecording, sentenceRecording = ')
      state.logger?.debug && state.logger.debug(sentenceRecording)
      const sentenceId = sentenceRecording.sentence.id
      if (!(sentenceId in state.recordings)) {
        state.recordings[sentenceId] = {}
      }

      if (!(sentenceRecording.id in state.recordings[sentenceId])) {
        state.recordings[sentenceId][sentenceRecording.id] = {
          // can't use DEFAULT_PITCH_SHIFT, because that will end up being the literal key
          audio: {
            [DEFAULT_PITCH_SHIFT]: cloneRaw(nullPitchedSentenceOrSegmentAudio), // no pitch shift
          },
          downloading: false,
          sentenceRecording: null,
          updating: false,
        } as SentenceRecordingMetadataWithPitchedAudios
        state.logger?.info && state.logger.info(`added sentenceRecording.id ${sentenceRecording.id} to state.recordings[sentenceId]`)
      } else {
        state.logger?.info && state.logger.info('sentenceRecording.id already in state.recordings[sentenceId]')
      }

      // store sentenceRecording
      state.recordings[sentenceId][sentenceRecording.id].sentenceRecording = cloneRaw(sentenceRecording)

      // sort stored tags
      state.recordings[sentenceId][sentenceRecording.id].sentenceRecording!.tags.sort((a: TagType, b: TagType) => {
        return a.name.localeCompare(b.name)
      })
    },

    deleteRecording(state: RecordingsState, { sentenceRecording }: { sentenceRecording: SentenceRecordingType }) {
      const { id: sentenceId } = sentenceRecording.sentence
      const { id: sentenceRecordingId } = sentenceRecording
      const recording = state.recordings[sentenceId]?.[sentenceRecordingId]
      if (!recording) {
        return
      }
      releaseRecordingAudio(recording)
      delete state.recordings[sentenceId][sentenceRecordingId]
    },

    deleteRecordingById(state: RecordingsState, sentenceRecordingId: string) {
      for (const [sentenceId, byRecording] of Object.entries(state.recordings)) {
        if (sentenceRecordingId in byRecording) {
          releaseRecordingAudio(byRecording[sentenceRecordingId])
          delete state.recordings[sentenceId][sentenceRecordingId]
          return
        }
      }
    },

    deleteSentence(state: RecordingsState, payload: { sentence: SentenceType }) {
      const sentenceId = payload.sentence.id
      if (!(sentenceId in state.recordings)) {
        console.error('WARNING: deleteSentence with unknown sentenceId')
        return
      }

      if (Object.keys(state.recordings[sentenceId]).length) {
        console.error('WARNING: deleteSentence with non-empty store')
        return
      }
      delete state.recordings[sentenceId]
    },

    initPitchedAudio(state: RecordingsState, payload: { sentenceId: string; sentenceRecordingId: string; pitchShift: PitchShiftType }) {
      const { pitchShift, sentenceId, sentenceRecordingId } = payload
      if (sentenceId in state.recordings && sentenceRecordingId in state.recordings[sentenceId]) {
        state.recordings[sentenceId][sentenceRecordingId].audio[pitchShift] = cloneRaw(nullPitchedSentenceOrSegmentAudio)
      }
    },

    initSegmentSlots(
      state: RecordingsState,
      payload: { sentenceId: string; sentenceRecordingId: string; pitchShift: PitchShiftType; segmentCount: number },
    ) {
      const { pitchShift, segmentCount, sentenceId, sentenceRecordingId } = payload
      const recording = state.recordings[sentenceId]?.[sentenceRecordingId]
      if (!recording || !(pitchShift in recording.audio)) {
        return
      }
      const pitchedAudio = recording.audio[pitchShift]
      pitchedAudio.segmentHTMLAudioWrappers.splice(0)
      for (let i = 0; i < segmentCount; i++) {
        pitchedAudio.segmentHTMLAudioWrappers.push({} as HTMLAudioWrapper)
      }
    },

    modifySentenceRecording(
      state: RecordingsState,
      sentenceRecordingMetadataWithPitchedAudios: SentenceRecordingMetadataWithPitchedAudios,
    ) {
      const sentenceId = sentenceRecordingMetadataWithPitchedAudios.sentenceRecording?.sentence.id
      if (!sentenceId) {
        return
      }

      if (!(sentenceId in state.recordings)) {
        console.error('WARNING: store.audio, modifyRecording with unknown sentenceId')
        return
      }

      const sentenceRecordingId = sentenceRecordingMetadataWithPitchedAudios.sentenceRecording?.id
      if (!sentenceRecordingId) {
        return
      }
      state.recordings[sentenceId][sentenceRecordingId] = sentenceRecordingMetadataWithPitchedAudios
    },

    reset(state: RecordingsState) {
      Object.assign(state, { ...resetState() })
    },

    setFetchingAudio(state, payload: { sentenceRecording: SentenceRecordingType; b: boolean }) {
      const sentenceId = payload.sentenceRecording.sentence.id
      if (!(sentenceId in state.recordings)) {
        console.error('WARNING: store.audio, setFetchingAudio with unknown sentenceId')
        return
      }

      const sentenceRecordingId = payload.sentenceRecording.id
      if (!(sentenceRecordingId in state.recordings[sentenceId])) {
        console.error('WARNING: store.audio, setFetchingAudio with unknown sentenceRecordingId')
        return
      }

      state.loading = payload.b
      state.recordings[sentenceId][sentenceRecordingId].downloading = payload.b
    },

    setLoading(state: RecordingsState, loading: boolean) {
      state.loading = loading
    },

    setLogger(state: RecordingsState, logger: VueLogger) {
      state.logger = logger
    },

    setUpdating(state: RecordingsState, updating: boolean) {
      state.updating = updating
    },

    setUpdatingState(
      state: RecordingsState,
      payload: {
        sentenceRecordingMetadataWithPitchedAudios: SentenceRecordingMetadataWithPitchedAudios
        b: boolean
      },
    ) {
      const sentenceId = payload.sentenceRecordingMetadataWithPitchedAudios.sentenceRecording?.sentence.id
      if (!sentenceId) {
        return
      }

      if (!(sentenceId in state.recordings)) {
        console.error('WARNING: store.audio, modifyRecording with unknown sentenceId')
        return
      }

      const sentenceRecordingId = payload.sentenceRecordingMetadataWithPitchedAudios.sentenceRecording?.id

      if (!sentenceRecordingId) {
        return
      }

      if (!(sentenceRecordingId in state.recordings[sentenceId])) {
        console.error('WARNING: store.audio, modifyRecording with unknown sentenceRecordingId')
        return
      }

      state.updating = payload.b
      state.recordings[sentenceId][sentenceRecordingId].updating = payload.b
    },
  },

  namespaced: true as const,

  state: () => resetState(),
})

export default audio
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const audioGetterContext = (args: [any, any, any, any]) => localGetterContext(args, audio)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const audioActionContext = (context: any) => localActionContext(context, audio)
